import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { MessagesSquare, Users } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHAT_MESSAGE_MAX_LENGTH,
  ChatConversationDetailSchema,
  ChatConversationSchema,
  ChatMessageSchema,
  ChatSettingsSchema,
  ChatUnreadSchema,
  DocumentSchema,
  fileSizeLabel,
  type ChatConversationDetailDto,
  type ChatMessageDto,
  type ChatPersonDto,
  type ChatSettingsDto,
  type MeResponse,
} from '@taqon/contracts';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router';
import { errorMessage, getData, newIdempotencyKey, postForm } from '../../shared/api/client';
import { DOCUMENTS, DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { oversizeMessage, useUploadLimit } from '../../shared/documents/upload-limit';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { PersonAvatar } from '../../shared/ui/person';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '../../shared/ui/page';
import { absoluteTime, relativeTime } from '../notifications/inbox';
import {
  CHAT,
  CONVERSATION_LIST_POLL_MS,
  MESSAGE_PAGE,
  OPEN_CONVERSATION_POLL_MS,
  conversationKey,
  conversationsKey,
  mergeMessages,
  placeholderFor,
  unreadKey,
  useChatActions,
} from './chat-api';
import { LocalAudio, MessageAttachments } from './ChatAttachments';
import { MentionTextField, keptMentions } from './MentionTextField';
import { PersonPicker } from './PersonPicker';
import { NewGroupDialog } from './ChatGroupsPage';
import {
  VOICE_MAX_SECONDS,
  durationLabel,
  recorderFormat,
  useVoiceRecorder,
  type VoiceRecording,
} from './voice';

const ConversationsSchema = z.array(ChatConversationSchema);
const MessagesSchema = z.array(ChatMessageSchema);
const CHAT_MESSAGE = 'platform.chat_message';
/** Files one message may carry when it is sent; more can be added from its attachments. */
export const MAX_FILES_PER_MESSAGE = 10;

/** A file chosen in the composer, or a voice recording, waiting for Send. */
interface PendingFile {
  readonly key: string;
  readonly file: File;
  /** Set for a voice recording: its length in seconds. */
  readonly voiceSeconds?: number;
}

/** The title the document gets: a voice message's length, or the file's own name. */
function titleOf(pending: PendingFile): string {
  return pending.voiceSeconds === undefined
    ? pending.file.name.slice(0, 200)
    : `Voice message (${durationLabel(pending.voiceSeconds)})`;
}

/**
 * The text of a message sent with files but nothing typed: every message has text (SDD §29.2), and
 * this one says what it carries.
 */
export function attachmentOnlyText(pending: readonly PendingFile[]): string {
  const first = pending[0];
  if (pending.length === 1 && first?.voiceSeconds !== undefined) return 'Voice message';
  const names = pending.map((p) => (p.voiceSeconds === undefined ? p.file.name : 'voice message'));
  const text = `Attached: ${names.join(', ')}`;
  return text.length <= CHAT_MESSAGE_MAX_LENGTH
    ? text
    : `${text.slice(0, CHAT_MESSAGE_MAX_LENGTH - 1)}…`;
}

/**
 * Internal chat (FRD §4.9; SDD §29.11): the caller's conversations on the left, the open one on
 * the right. Only conversations the caller takes part in exist here; the API refuses everything
 * else (SDD §29.3).
 */
export function ChatPage() {
  const session = useSession();
  const me = session.data;
  const { conversationId } = useParams();
  if (me === null || me === undefined) return null;
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Chat"
        description="One-to-one and group conversations with colleagues. Group chats are set up by a chat administrator."
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'stretch' }}>
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <ConversationList me={me} selectedId={conversationId ?? null} />
        </Paper>
        <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0 }}>
          {conversationId === undefined ? (
            <Box sx={{ p: 4 }}>
              <EmptyState
                title="No conversation open"
                detail="Choose a conversation, or start a new chat."
              />
            </Box>
          ) : (
            <ConversationView key={conversationId} me={me} conversationId={conversationId} />
          )}
        </Paper>
      </Stack>
    </Stack>
  );
}

/** The navigation icon for Chat, with the caller's unread count (SDD §29.11). */
export function ChatNavIcon() {
  const unread = useQuery({
    queryKey: unreadKey,
    queryFn: () => getData(`${CHAT}/unread-count`, ChatUnreadSchema),
    refetchInterval: CONVERSATION_LIST_POLL_MS,
    refetchOnWindowFocus: true,
  });
  return (
    <Badge badgeContent={unread.data?.unread ?? 0} color="error" max={99}>
      <MessagesSquare />
    </Badge>
  );
}

function ConversationList({ me, selectedId }: { me: MeResponse; selectedId: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const canStart = hasCapability(me, 'platform.conversation:create');
  // Only a chat administrator creates groups (FRD-CORE-048); everyone else sees no such button.
  const canCreateGroup = hasCapability(me, 'platform.chat_group:create');
  const conversations = useQuery({
    queryKey: conversationsKey,
    queryFn: () => getData(`${CHAT}/conversations`, ConversationsSchema),
    refetchInterval: CONVERSATION_LIST_POLL_MS,
    refetchOnWindowFocus: true,
  });

  return (
    <Stack>
      <Stack
        direction="row"
        sx={{
          px: 2,
          py: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle1" component="h2">
          Conversations
        </Typography>
        <Stack direction="row" spacing={1} sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          {canCreateGroup && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setGrouping(true);
              }}
            >
              New group
            </Button>
          )}
          {canStart && (
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setStarting(true);
              }}
            >
              New chat
            </Button>
          )}
        </Stack>
      </Stack>
      <Divider />
      {conversations.isPending && <LoadingState label="Loading conversations" />}
      {conversations.isError && <ErrorState message={errorMessage(conversations.error)} />}
      {conversations.data?.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No conversations yet.
          </Typography>
        </Box>
      )}
      {conversations.data !== undefined && conversations.data.length > 0 && (
        <List aria-label="Conversations" dense disablePadding>
          {conversations.data.map((conversation) => (
            <ListItem key={conversation.id} disablePadding>
              <ListItemButton
                selected={conversation.id === selectedId}
                onClick={() => {
                  void navigate(`/chat/${encodeURIComponent(conversation.id)}`);
                }}
              >
                <Box sx={{ mr: 1.5, display: 'flex' }}>
                  <ConversationAvatar conversation={conversation} size={28} />
                </Box>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: conversation.unreadCount > 0 ? 700 : 400 }}
                        noWrap
                      >
                        {conversation.label}
                      </Typography>
                      {conversation.kind === 'group' && (
                        <Chip size="small" variant="outlined" label="Group" />
                      )}
                      {conversation.status === 'archived' && <Chip size="small" label="Archived" />}
                    </Stack>
                  }
                  secondary={
                    conversation.lastMessageAt === null
                      ? 'No messages yet'
                      : relativeTime(conversation.lastMessageAt)
                  }
                />
                {conversation.unreadCount > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={conversation.unreadCount}
                    aria-label={`${String(conversation.unreadCount)} unread`}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      <NewChatDialog
        me={me}
        open={starting}
        onClose={() => {
          setStarting(false);
        }}
      />
      {grouping && (
        <NewGroupDialog
          me={me}
          initialMembers={[{ userId: me.userId, displayName: me.displayName }]}
          onClose={() => {
            setGrouping(false);
          }}
          onCreated={(group) => {
            setGrouping(false);
            void queryClient.invalidateQueries({ queryKey: conversationsKey });
            // Opened in Chat when the creator is a member; otherwise only its admin page can show it.
            void navigate(
              group.members.some((m) => m.userId === me.userId)
                ? `/chat/${encodeURIComponent(group.id)}`
                : `/admin/chat-groups/${encodeURIComponent(group.id)}`,
            );
          }}
        />
      )}
    </Stack>
  );
}

function NewChatDialog({
  me,
  open,
  onClose,
}: {
  me: MeResponse;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { start } = useChatActions(me.csrfToken);
  const [person, setPerson] = useState<ChatPersonDto | null>(null);
  const close = () => {
    setPerson(null);
    start.reset();
    onClose();
  };
  return (
    <FormDialog
      open={open}
      title="New chat"
      submitLabel="Start chat"
      canSubmit={person !== null}
      submitting={start.isPending}
      error={start.error === null ? null : errorMessage(start.error)}
      onClose={close}
      onSubmit={() => {
        if (person === null) return;
        start.mutate(person.userId, {
          onSuccess: (conversation) => {
            close();
            void navigate(`/chat/${encodeURIComponent(conversation.id)}`);
          },
        });
      }}
    >
      <PersonPicker
        label="Person"
        value={person}
        onChange={setPerson}
        exclude={[me.userId]}
        autoFocus
      />
    </FormDialog>
  );
}

function ConversationView({ me, conversationId }: { me: MeResponse; conversationId: string }) {
  const queryClient = useQueryClient();
  const { post, edit, remove, markRead } = useChatActions(me.csrfToken);
  const [older, setOlder] = useState<ChatMessageDto[]>([]);
  const [noMoreOlder, setNoMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [editing, setEditing] = useState<ChatMessageDto | null>(null);
  const [deleting, setDeleting] = useState<ChatMessageDto | null>(null);
  const [attachmentsOf, setAttachmentsOf] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [notAttached, setNotAttached] = useState<readonly string[]>([]);
  const lastMarked = useRef<string | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);
  const base = `${CHAT}/conversations/${encodeURIComponent(conversationId)}`;

  const detail = useQuery({
    queryKey: conversationKey(conversationId),
    queryFn: () => getData(base, ChatConversationDetailSchema),
    refetchInterval: CONVERSATION_LIST_POLL_MS,
  });
  // The newest page, polled while this tab is visible: new messages, and edits or deletions of
  // recent ones, arrive within a few seconds (SDD §29.9).
  const latest = useQuery({
    queryKey: [...conversationKey(conversationId), 'latest'],
    queryFn: () => getData(`${base}/messages?limit=${String(MESSAGE_PAGE)}`, MessagesSchema),
    refetchInterval: OPEN_CONVERSATION_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const settings = useQuery({
    queryKey: ['chat', 'settings'],
    queryFn: () => getData(`${CHAT}/settings`, ChatSettingsSchema),
  });

  const messages = mergeMessages(older, latest.data ?? []);
  const newestId = messages.at(-1)?.id ?? null;

  useEffect(() => {
    if (newestId === null || newestId === lastMarked.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    lastMarked.current = newestId;
    markRead.mutate({ conversationId, lastMessageId: newestId });
    try {
      bottom.current?.scrollIntoView({ block: 'end' });
    } catch {
      // Not every environment scrolls (jsdom has no layout); the messages are there regardless.
    }
    // markRead is stable enough for this purpose; re-running on it would re-mark needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestId, conversationId]);

  const refreshMessages = () =>
    queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });

  const loadOlder = async () => {
    const oldest = messages[0]?.id;
    if (oldest === undefined) return;
    setLoadingOlder(true);
    try {
      const page = await getData(
        `${base}/messages?before=${encodeURIComponent(oldest)}&limit=${String(MESSAGE_PAGE)}`,
        MessagesSchema,
      );
      setOlder((current) => mergeMessages(current, page));
      if (page.length < MESSAGE_PAGE) setNoMoreOlder(true);
    } finally {
      setLoadingOlder(false);
    }
  };

  if (detail.isPending) return <LoadingState label="Loading conversation" />;
  if (detail.isError) return <ErrorState message={errorMessage(detail.error)} />;
  const conversation = detail.data;

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack direction="row" spacing={1.5} sx={{ px: 2, py: 1.5, alignItems: 'center' }}>
        <ConversationAvatar conversation={conversation} size={32} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2">
            {conversation.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {conversation.participants.map((p) => p.displayName).join(', ')}
          </Typography>
        </Box>
      </Stack>
      <Divider />
      {conversation.status === 'archived' && (
        <Alert severity="info" sx={{ m: 2 }}>
          This group chat is archived. It stays readable but no one can post.
        </Alert>
      )}
      <Box
        role="log"
        aria-label={`Messages in ${conversation.label}`}
        sx={{ p: 2, overflowY: 'auto', maxHeight: '60vh', minHeight: 240 }}
      >
        {latest.isPending && <LoadingState label="Loading messages" />}
        {latest.isError && <ErrorState message={errorMessage(latest.error)} />}
        {latest.data !== undefined && latest.data.length >= MESSAGE_PAGE && !noMoreOlder && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button size="small" disabled={loadingOlder} onClick={() => void loadOlder()}>
              Load earlier messages
            </Button>
          </Box>
        )}
        {latest.data !== undefined && messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No messages yet. Say hello.
          </Typography>
        )}
        <Stack spacing={1.5}>
          {messages.map((message) => (
            <MessageItem
              key={message.id}
              me={me}
              message={message}
              canChange={conversation.canPost}
              attachmentsOpen={attachmentsOf === message.id}
              canAttach={
                message.mine &&
                conversation.canPost &&
                settings.data?.attachmentDocumentTypeCode !== null &&
                settings.data !== undefined
              }
              onToggleAttachments={() => {
                setAttachmentsOf((current) => (current === message.id ? null : message.id));
              }}
              onEdit={() => {
                setEditing(message);
              }}
              onDelete={() => {
                setDeleting(message);
              }}
            />
          ))}
        </Stack>
        <div ref={bottom} />
      </Box>
      <Divider />
      {conversation.canPost ? (
        <Composer
          conversation={conversation}
          me={me}
          settings={settings.data}
          sending={post.isPending || uploading}
          error={post.error === null ? null : errorMessage(post.error)}
          notAttached={notAttached}
          onSend={(body, mentionUserIds, files, done) => {
            setNotAttached([]);
            post.mutate(
              { conversationId, body, mentionUserIds },
              {
                onSuccess: (message) => {
                  done();
                  void refreshMessages();
                  const typeCode = settings.data?.attachmentDocumentTypeCode;
                  if (files.length === 0 || typeCode === null || typeCode === undefined) return;
                  setUploading(true);
                  void attachAll(message.id, typeCode, files, me.csrfToken)
                    .then(setNotAttached)
                    .finally(() => {
                      setUploading(false);
                      void refreshMessages();
                    });
                },
              },
            );
          }}
        />
      ) : null}
      <EditMessageDialog
        key={editing?.id ?? 'none'}
        message={editing}
        people={conversation.participants.filter((p) => p.userId !== me.userId)}
        submitting={edit.isPending}
        error={edit.error === null ? null : errorMessage(edit.error)}
        onClose={() => {
          setEditing(null);
          edit.reset();
        }}
        onSubmit={(message, body, mentionUserIds) => {
          edit.mutate(
            { message, body, mentionUserIds },
            {
              onSuccess: () => {
                setEditing(null);
                void refreshMessages();
              },
            },
          );
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete message"
        message="The message will be hidden from everyone in this chat. This cannot be undone."
        confirmLabel="Delete"
        tone="error"
        busy={remove.isPending}
        error={remove.error === null ? null : errorMessage(remove.error)}
        onCancel={() => {
          setDeleting(null);
          remove.reset();
        }}
        onConfirm={() => {
          if (deleting === null) return;
          remove.mutate(deleting, {
            onSuccess: () => {
              setDeleting(null);
              void refreshMessages();
            },
          });
        }}
      />
    </Stack>
  );
}

/** The other person's photo for a one-to-one conversation; a group icon for a group. */
function ConversationAvatar({
  conversation,
  size,
}: {
  conversation: { otherUser: ChatPersonDto | null };
  size: number;
}) {
  if (conversation.otherUser !== null) {
    return (
      <PersonAvatar
        name={conversation.otherUser.displayName}
        userId={conversation.otherUser.userId}
        size={size}
      />
    );
  }
  return (
    <Avatar
      aria-hidden
      sx={{ width: size, height: size, color: 'text.secondary', bgcolor: 'action.selected' }}
    >
      <Users size={Math.round(size * 0.55)} />
    </Avatar>
  );
}

function MessageItem({
  me,
  message,
  canChange,
  canAttach,
  attachmentsOpen,
  onToggleAttachments,
  onEdit,
  onDelete,
}: {
  me: MeResponse;
  message: ChatMessageDto;
  canChange: boolean;
  canAttach: boolean;
  attachmentsOpen: boolean;
  onToggleAttachments: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const placeholder = placeholderFor(message);
  const timeZone = me.businessTimezone;
  return (
    <Stack
      component="article"
      direction="row"
      spacing={1}
      aria-label={`Message from ${message.author.displayName}`}
      sx={{
        alignSelf: message.mine ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        maxWidth: '85%',
      }}
    >
      {!message.mine && (
        <PersonAvatar name={message.author.displayName} userId={message.author.userId} size={30} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: message.mine ? 'action.hover' : 'background.paper',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
            <Typography variant="subtitle2" component="span">
              {message.mine ? 'You' : message.author.displayName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              title={absoluteTime(message.postedAt, timeZone)}
            >
              {relativeTime(message.postedAt)}
              {message.editedAt !== null && placeholder === null ? ' · edited' : ''}
            </Typography>
          </Stack>
          {placeholder === null ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {message.body}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {placeholder}
            </Typography>
          )}
          {placeholder === null && <MessageAttachments attachments={message.attachments} />}
          {message.mentions.length > 0 && placeholder === null && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {message.mentions.map((person) => (
                <Chip key={person.userId} size="small" label={`@${person.displayName}`} />
              ))}
            </Stack>
          )}
          {placeholder === null && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              <Button size="small" onClick={onToggleAttachments} aria-expanded={attachmentsOpen}>
                Attachments
              </Button>
              {message.mine && canChange && (
                <>
                  <Button size="small" onClick={onEdit}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={onDelete}>
                    Delete
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Paper>
        <Collapse in={attachmentsOpen} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <DocumentsPanel
              recordType={CHAT_MESSAGE}
              recordId={message.id}
              canAttach={canAttach}
              title="Attachments"
            />
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
}

/**
 * The files of a message just posted, uploaded one by one to it (FRD-CORE-050): each is a document
 * of the chat attachment type, scanned before anyone can open it (SDD §29.7). Returns the files
 * that could not be attached, with the reason.
 */
async function attachAll(
  messageId: string,
  documentTypeCode: string,
  files: readonly PendingFile[],
  csrfToken: string,
): Promise<string[]> {
  const failed: string[] = [];
  for (const pending of files) {
    const form = new FormData();
    form.append('ownerRecordType', CHAT_MESSAGE);
    form.append('ownerRecordId', messageId);
    form.append('documentTypeCode', documentTypeCode);
    form.append('title', titleOf(pending));
    // The file goes last: the API authorizes from the fields before it stores anything.
    form.append('file', pending.file, pending.file.name);
    try {
      await postForm(DOCUMENTS, form, DocumentSchema, csrfToken, {
        idempotencyKey: newIdempotencyKey(),
      });
    } catch (error) {
      failed.push(
        `${titleOf(pending)}: ${errorMessage(error instanceof Error ? error : new Error(String(error)))}`,
      );
    }
  }
  return failed;
}

function Composer({
  conversation,
  me,
  settings,
  sending,
  error,
  notAttached,
  onSend,
}: {
  conversation: ChatConversationDetailDto;
  me: MeResponse;
  settings: ChatSettingsDto | undefined;
  sending: boolean;
  error: string | null;
  /** Files of the last message sent that could not be attached, with the reason. */
  notAttached: readonly string[];
  onSend: (
    body: string,
    mentionUserIds: readonly string[],
    files: readonly PendingFile[],
    done: () => void,
  ) => void;
}) {
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<ChatPersonDto[]>([]);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const limit = useUploadLimit();
  const others = conversation.participants.filter((p) => p.userId !== me.userId);
  const trimmed = body.trim();
  const accepted = settings?.attachmentContentTypes ?? [];
  const canAttach = settings?.attachmentDocumentTypeCode != null && accepted.length > 0;
  const format = canAttach ? recorderFormat(accepted) : null;
  const room = MAX_FILES_PER_MESSAGE - files.length;

  const add = (chosen: readonly File[], voiceSeconds?: number) => {
    const problems: string[] = [];
    const fitting = chosen.filter((file) => {
      const problem = oversizeMessage(file, limit);
      if (problem !== null) problems.push(`${file.name}: ${problem}`);
      return problem === null;
    });
    if (fitting.length > room) {
      problems.push(
        `A message can carry up to ${String(MAX_FILES_PER_MESSAGE)} files; add more from its Attachments after sending.`,
      );
    }
    setFileProblem(problems.length === 0 ? null : problems.join(' '));
    setFiles((current) => [
      ...current,
      ...fitting.slice(0, Math.max(0, room)).map((file) => ({
        key: newIdempotencyKey(),
        file,
        ...(voiceSeconds === undefined ? {} : { voiceSeconds }),
      })),
    ]);
  };
  const voice = useVoiceRecorder(format, (recording: VoiceRecording) => {
    add([recording.file], recording.seconds);
  });

  const canSend = (trimmed.length > 0 || files.length > 0) && !sending && !voice.recording;
  const send = () => {
    if (!canSend) return;
    const text = trimmed.length > 0 ? trimmed : attachmentOnlyText(files);
    // A mention whose "@name" was deleted from the text is not sent.
    onSend(
      text,
      keptMentions(mentions, text).map((m) => m.userId),
      files,
      () => {
        setBody('');
        setMentions([]);
        setFiles([]);
        setFileProblem(null);
      },
    );
  };
  return (
    <Box
      component="form"
      aria-label="Write a message"
      sx={{ p: 2 }}
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <Stack spacing={1.5}>
        {error !== null && <Alert severity="error">{error}</Alert>}
        {notAttached.length > 0 && (
          <Alert severity="warning">
            The message was sent, but these files could not be attached: {notAttached.join('; ')}.
            Open the message&apos;s Attachments to try again.
          </Alert>
        )}
        <MentionTextField
          id={conversation.id}
          people={others}
          value={body}
          onChange={setBody}
          onMention={(person) => {
            setMentions((current) =>
              current.some((m) => m.userId === person.userId) ? current : [...current, person],
            );
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              send();
            }
          }}
          helperText={
            others.length > 0
              ? 'Type @ to mention someone in this chat. Ctrl+Enter sends. Do not share salary, identity or bank details in chat.'
              : 'Ctrl+Enter sends. Do not share salary, identity or bank details in chat.'
          }
        />
        {files.length > 0 && (
          <Stack
            component="ul"
            aria-label="To send with the message"
            spacing={1}
            sx={{ listStyle: 'none', p: 0, m: 0 }}
          >
            {files.map((pending) => (
              <Stack
                component="li"
                key={pending.key}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                {pending.voiceSeconds === undefined ? (
                  <Chip
                    icon={<AttachFileIcon />}
                    label={`${pending.file.name} · ${fileSizeLabel(pending.file.size)}`}
                    variant="outlined"
                    sx={{ maxWidth: '100%' }}
                  />
                ) : (
                  <>
                    <Typography variant="body2">{titleOf(pending)}</Typography>
                    <LocalAudio file={pending.file} label={titleOf(pending)} />
                  </>
                )}
                <IconButton
                  size="small"
                  aria-label={`Remove ${titleOf(pending)}`}
                  disabled={sending}
                  onClick={() => {
                    setFiles((current) => current.filter((f) => f.key !== pending.key));
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        {fileProblem !== null && (
          <Typography variant="caption" color="error" role="alert">
            {fileProblem}
          </Typography>
        )}
        {voice.error !== null && (
          <Typography variant="caption" color="error" role="alert">
            {voice.error}
          </Typography>
        )}
        {voice.recording && (
          <Stack
            direction="row"
            spacing={1}
            role="status"
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <FiberManualRecordIcon color="error" fontSize="small" />
            <Typography variant="body2">
              Recording {durationLabel(voice.seconds)} of {durationLabel(VOICE_MAX_SECONDS)}
            </Typography>
            <Button size="small" variant="contained" startIcon={<StopIcon />} onClick={voice.stop}>
              Stop
            </Button>
            <Button size="small" onClick={voice.discard}>
              Discard
            </Button>
          </Stack>
        )}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', rowGap: 1 }}
        >
          {canAttach && (
            <>
              <Box
                component="input"
                ref={picker}
                type="file"
                multiple
                hidden
                accept={accepted.join(',')}
                aria-label="Files to attach"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  add([...(event.target.files ?? [])]);
                  // Choosing the same file again still reports a change.
                  event.target.value = '';
                }}
              />
              <Button
                startIcon={<AttachFileIcon />}
                disabled={sending || room <= 0}
                onClick={() => picker.current?.click()}
              >
                Attach files
              </Button>
            </>
          )}
          {format !== null && !voice.recording && (
            <Button
              startIcon={<MicIcon />}
              disabled={sending || room <= 0}
              onClick={() => void voice.start()}
            >
              Record voice
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            type="submit"
            variant="contained"
            disabled={!canSend}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            {sending && files.length > 0 ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
        {canAttach && limit !== undefined && (
          <Typography variant="caption" color="text.secondary">
            Up to {String(MAX_FILES_PER_MESSAGE)} files per message, each up to{' '}
            {fileSizeLabel(limit)}. Files are checked for viruses before others can open them.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The author edits their message (FRD-CORE-051). Typing "@" offers the chat's other participants
 * as in the composer (FRD-CORE-053): a person added is notified as a mention. People already
 * mentioned stay mentioned — they were told — so the dialog sends them with any added.
 */
function EditMessageDialog({
  message,
  people,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  message: ChatMessageDto | null;
  /** The other participants, who may be mentioned. */
  people: readonly ChatPersonDto[];
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (message: ChatMessageDto, body: string, mentionUserIds: readonly string[]) => void;
}) {
  // Remounted per message (its `key`), so it starts from that message's text and mentions.
  const [body, setBody] = useState(message?.body ?? '');
  const [added, setAdded] = useState<ChatPersonDto[]>([]);
  const trimmed = body.trim();
  const existing = message?.mentions ?? [];
  const mentionUserIds = [
    ...new Set([...existing, ...keptMentions(added, trimmed)].map((m) => m.userId)),
  ];
  return (
    <FormDialog
      open={message !== null}
      title="Edit message"
      submitLabel="Save"
      canSubmit={trimmed.length > 0 && trimmed !== message?.body}
      submitting={submitting}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        if (message !== null) onSubmit(message, trimmed, mentionUserIds);
      }}
    >
      <MentionTextField
        id={`edit-${message?.id ?? 'none'}`}
        people={people}
        value={body}
        onChange={setBody}
        onMention={(person) => {
          setAdded((current) =>
            current.some((m) => m.userId === person.userId) ? current : [...current, person],
          );
        }}
        autoFocus
        helperText={people.length > 0 ? 'Type @ to mention someone in this chat.' : undefined}
      />
    </FormDialog>
  );
}
